import { promoStrings } from "./appPromoStrings";

/**
 * План, календарь и загрузка файла с часов — только в приложении (владелец,
 * 03.10: «План, календарь и загрузку файлов на сайте — это не нужно никому
 * показывать на сайте, но сделай, чтобы можно было загружать эти файлы в
 * приложении… Также убери упоминания о календаре на сайте, сделай календарь
 * в приложении»). Тестовая тренировка ушла следом (владелец, 07.10: «можно
 * сделать через приложение бесплатно если тренировка тестовая… нужно убрать
 * с сайта»): на сайте из тренировок не остаётся ничего, кроме карточки
 * приложения.
 *
 * Один рубильник на всё:
 *  - "off"     — как было до 03.10: страницы на месте, у админа на карточке
 *                приложения строка «старая версия» (откат — одно слово);
 *  - "preview" — скрыто там, где включено новое меню (админ, не «смотреть как
 *                пользователь»): так владелец смотрел раньше всех;
 *  - "all"     — скрыто у всех (сейчас): «Мои тренировки» прежнего меню ведут
 *                на «Тренировки» — карточку приложения; «Календарь»,
 *                «Загрузить тренировку» и «Составить план» из меню ушли.
 *                Инструкции к приложениям (ManualPage/content) на сайтовый
 *                календарь больше не ссылаются — это стережёт
 *                ManualPage/content/siteWorkoutPages.test.ts.
 *
 * Скрытые страницы по прямому адресу (старые ссылки, закладки, советы лиги,
 * достижения) открывают «Тренировки» — карточку приложения с тестовой
 * тренировкой (SiteWorkoutPageGate). /account/calendar — вкладка «Календарь»
 * (владелец, 20.10): та же карточка со своим заголовком, а без оболочки и
 * меню V5 — переход на «Тренировки» (AppOnlySectionPage).
 */
export type SiteWorkoutPagesMode = "off" | "preview" | "all";

export const SITE_WORKOUT_PAGES_HIDDEN: SiteWorkoutPagesMode = "all";

/**
 * Скрываемые страницы: календарь /account/workouts, план /account/plan-create,
 * файл /account/fit-upload, тестовая тренировка /account/testing-workout.
 */
export type HiddenWorkoutPage = "calendar" | "plan" | "upload" | "test";

/**
 * Окно приложения (?source=app) оставляет открытой одну страницу — загрузку
 * файла /account/fit-upload: кнопка «Загрузить тренировку» приложения v5
 * (SitePages.openFitUpload) открывает именно её. Календарь и план скрыты и
 * там — в приложении свои.
 *
 * Убрать (поставить false), когда загрузка файла станет своей в приложении
 * (экран «Загрузить тренировку» → site_server /api/workout-files) во ВСЕХ
 * выпущенных сборках, которые открывают /account/fit-upload: тогда этот адрес
 * ведёт на «Тренировки», как остальные.
 */
export const APP_WINDOW_KEEPS_FIT_UPLOAD = true;

/** Открыта ли страница в окне приложения при скрытом рубильнике. */
export const appWindowKeeps = (page: HiddenWorkoutPage): boolean => page === "upload" && APP_WINDOW_KEEPS_FIT_UPLOAD;

/** Куда ведут скрытые страницы: «Тренировки» — карточка приложения с тестовой тренировкой. */
export const hiddenWorkoutPageTarget = (lang: string | undefined | null): string => `/${lang || "ru"}/account/training`;

/** Прежние адреса скрываемых страниц (без языка). */
const OLD_PATH: Record<HiddenWorkoutPage, string> = {
    calendar: "/account/workouts",
    plan: "/account/plan-create",
    upload: "/account/fit-upload",
    test: "/account/testing-workout",
};

/**
 * Адрес (без языка) для ссылок сайта «туда, где тренировки»: «К тренировкам»
 * достижений, «Неделя по плану» и «Составить план» лиги, «Подробнее» баллов,
 * покупка подписки… В "all" — «Тренировки» (карточка приложения), иначе —
 * прежняя страница: откат рубильника возвращает и ссылки.
 */
export const workoutPagePath = (page: HiddenWorkoutPage, mode: SiteWorkoutPagesMode = SITE_WORKOUT_PAGES_HIDDEN): string =>
    mode === "all" ? "/account/training" : OLD_PATH[page];

/** То же с языком: «/ru/account/training». */
export const workoutPageHref = (
    page: HiddenWorkoutPage,
    lang: string | undefined | null,
    mode: SiteWorkoutPagesMode = SITE_WORKOUT_PAGES_HIDDEN,
): string => `/${lang || "ru"}${workoutPagePath(page, mode)}`;

/** Адрес сайта — «Тренировки» (с языком или без, с ?… и #… или без). */
export const isTrainingHref = (to: string): boolean => /^(\/[a-z]{2})?\/account\/training\/?([?#]|$)/i.test(to || "");

/**
 * Группа «Мои тренировки» прежнего меню (NavBar и MobileHeader) в режиме
 * "all": заголовок — на «Тренировки» (карточку приложения), первый пункт —
 * она же (бывший «Календарь»), «Загрузить тренировку» и «Составить план»
 * убраны. В остальных режимах — как есть.
 */
type OldMenuItem = { key: string; to: string; match: string; matchExact?: boolean; label?: unknown };
type OldMenuGroup = { headerTo?: string; headerMatch?: string; children: OldMenuItem[] };

const OLD_MENU_DROPPED: ReadonlySet<string> = new Set(["fit-upload", "plan-create", "testing-workout"]);

export const oldMenuWorkoutsGroup = <G extends OldMenuGroup>(
    group: G,
    lang: string,
    mode: SiteWorkoutPagesMode = SITE_WORKOUT_PAGES_HIDDEN,
): G => {
    if (mode !== "all") return group;
    const training = hiddenWorkoutPageTarget(lang);
    const label = promoStrings(lang)("training");
    return {
        ...group,
        headerTo: training,
        headerMatch: "/account/training",
        children: group.children
            .filter((c) => !OLD_MENU_DROPPED.has(c.key))
            .map((c) =>
                c.key === "calendar"
                    ? { ...c, key: "training", label, to: training, match: "/account/training", matchExact: true }
                    : c,
            ),
    } as G;
};
